import React, { useEffect, useState } from 'react';
import './Overview.css';
import { getBnbHostAnalytics } from '../../../api/services/analyticsService';

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const currency = (value) => `KES ${safeNumber(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const Overview = ({ onNavigate }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await getBnbHostAnalytics();
        if (mounted) setSnapshot(resp?.data || null);
      } catch (_) {
        // silently fail — still show zeros
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const snap = snapshot || {};
  const monthLabel = snap.currentMonth ? MONTH_NAMES[snap.currentMonth - 1] : '';
  const periodLabel = monthLabel && snap.currentYear ? `${monthLabel} ${snap.currentYear}` : 'This Month';

  const totalBookings =
    safeNumber(snap.bookingsPendingCount) +
    safeNumber(snap.bookingsConfirmedCount) +
    safeNumber(snap.bookingsCancelledCount) +
    safeNumber(snap.bookingsRejectedCount) +
    safeNumber(snap.bookingsCompletedCount);

  return (
    <div className="overview-container">
      {/* Welcome Section */}
      <div className="welcome-banner">
        <div className="welcome-content">
          <h1 className="welcome-title">Hello, welcome back! 👋</h1>
          <p className="welcome-subtitle">Manage your short-stay properties with ease. Track bookings, optimize occupancy rates, and maximize your rental income.</p>
        </div>
      </div>

      <h1 className="page-title">Dashboard</h1>

      {/* Key Metrics Cards */}
      <div className="metrics-row">
        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-house">🏠</div>
          <div className="metric-content">
            <div className="metric-label">Properties</div>
            <div className="metric-value">{safeNumber(snap.propertiesCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">{safeNumber(snap.unitsCount)} total units</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-dollar">💰</div>
          <div className="metric-content">
            <div className="metric-label">Revenue Paid ({periodLabel})</div>
            <div className="metric-value">{currency(snap.revenuePaidThisMonth)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">{currency(snap.revenuePendingThisMonth)} pending</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-check">📅</div>
          <div className="metric-content">
            <div className="metric-label">Bookings</div>
            <div className="metric-value">{totalBookings}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">{safeNumber(snap.bookingsPendingCount)} pending · {safeNumber(snap.bookingsConfirmedCount)} confirmed</span>
            </div>
          </div>
        </div>

        <div className={`metric-card ${loading ? 'metric-card-skeleton' : ''}`}>
          <div className="metric-icon metric-icon-check">🧑‍💼</div>
          <div className="metric-content">
            <div className="metric-label">Agents Assigned</div>
            <div className="metric-value">{safeNumber(snap.agentsAssignedCount)}</div>
            <div className="metric-trend metric-trend-neutral">
              <span className="trend-text">Assigned agents</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid – Bookings + Quick Actions */}
      <div className="main-content-grid">
        {/* Left – Bookings Breakdown */}
        <div className="content-left">
          <div className="content-card">
            <h3 className="content-card-title">Bookings Breakdown</h3>
            <div className="trust-factors" style={{ marginTop: '1rem' }}>
              {[
                { label: '⏳ Pending',   value: safeNumber(snap.bookingsPendingCount) },
                { label: '✅ Confirmed', value: safeNumber(snap.bookingsConfirmedCount) },
                { label: '🏁 Completed', value: safeNumber(snap.bookingsCompletedCount) },
                { label: '❌ Cancelled', value: safeNumber(snap.bookingsCancelledCount) },
                { label: '🚫 Rejected',  value: safeNumber(snap.bookingsRejectedCount) },
              ].map(({ label, value }) => (
                <div className="trust-factor-item" key={label} style={{ marginBottom: '0.75rem' }}>
                  <span className="trust-factor-text">{label}</span>
                  <span style={{ fontWeight: 700, marginLeft: 'auto' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right – Quick Actions */}
        <div className="content-right">
          <div className="pending-actions-card pending-actions-card-blue">
            <h3 className="pending-actions-title">Quick Actions</h3>
            <div className="pending-actions-list">
              {[
                { icon: '🏡', label: 'Manage Rentals', sub: 'View and manage your units',         section: 'units' },
                { icon: '📅', label: 'Bookings',        sub: 'Review pending and active bookings', section: 'bookings' },
                { icon: '📊', label: 'Analytics',       sub: 'View detailed performance stats',    section: 'analytics' },
                { icon: '🔔', label: 'Notifications',   sub: 'Check your latest notifications',   section: 'notifications' },
              ].map(({ icon, label, sub, section }) => (
                <div
                  key={section}
                  className="pending-action-item"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onNavigate && onNavigate(section)}
                >
                  <div className="action-info">
                    <div className="action-type">{icon} {label}</div>
                    <div className="action-count">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
